import type {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren, ReactNode, RefObject, useCallback, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ButtonChevron} from '../../../../../libs/shared/ui/src/components/buttons/buttonChevron';
import {ButtonChrome} from '../../../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {LoadingButton} from '../../../../../libs/shared/ui/src/components/buttons/loadingButton/loadingButton';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';

export interface InheritableTriggerButtonProps {
  renderDropdown?: (popoverProps: PopoverTransferProps) => ReactNode;
  onSelect?: () => void;
}

interface TriggerButtonProps extends InheritableTriggerButtonProps {
  isLoading?: boolean;
  title: ReactNode;
  renderPreview?: (anchor: RefObject<HTMLElement>, popperPlacement: Placement) => ReactNode;
  onDropdownToggle?: (isOpen: boolean) => void;
}

// Ideally, the button component should set the correct margins when no chevron or right icons are present
// but everything broke when I tried to update the margins.
interface StyledButtonTitleProps {
  $hasChevron: boolean;
}
const StyledButtonTitle = styled.div<StyledButtonTitleProps>`
  ${(p) =>
    !p.$hasChevron &&
    css`
      margin-right: 4px;
    `};
`;

const StyledButtonChevron = styled(ButtonChevron)`
  min-height: 100%;
`;

export const TriggerButton: FC<TriggerButtonProps> = ({
  title,
  onSelect,
  isLoading,
  renderPreview,
  renderDropdown,
  onDropdownToggle,
}) => {
  if (isLoading) {
    return (
      <Preview renderPreview={renderPreview}>
        <LoadingButton variant={ButtonVariantsEnum.SECONDARY} isDisabled isLoading>
          {title}
        </LoadingButton>
      </Preview>
    );
  }

  return (
    <ButtonChrome variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.SMALL}>
      {(buttonProps) => (
        <>
          <Preview renderPreview={renderPreview}>
            <ChromelessButton isDisabled={buttonProps.isDisabled} onClick={onSelect}>
              <ButtonContent leftIcon="lightning" {...buttonProps}>
                <StyledButtonTitle $hasChevron={Boolean(renderDropdown)}>{title}</StyledButtonTitle>
              </ButtonContent>
            </ChromelessButton>
          </Preview>

          {renderDropdown && (
            <TooltipCoordinator
              render={() => <Trans id="trigger-button-chevron-tooltip">View all macros</Trans>}
              hideOnMouseDown
            >
              <StyledButtonChevron
                {...buttonProps}
                renderDropdown={renderDropdown}
                onDropdownToggle={onDropdownToggle}
              />
            </TooltipCoordinator>
          )}
        </>
      )}
    </ButtonChrome>
  );
};

interface PreviewProps {
  renderPreview?: (anchor: RefObject<HTMLElement>, popperPlacement: Placement) => ReactNode;
}

const Preview: FC<PropsWithChildren<PreviewProps>> = ({children, renderPreview}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);

  const onEnter = useCallback(() => setShouldShowPreview(true), []);
  const onLeave = useCallback(() => setShouldShowPreview(false), []);

  if (!renderPreview) {
    return children;
  }

  return (
    <div ref={ref} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
      {shouldShowPreview && renderPreview(ref, 'bottom')}
    </div>
  );
};
